import { useState, useEffect, useCallback, useRef } from 'react';

export function usePolling<T>(fetcher: () => Promise<T>, intervalMs: number, deps: any[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [failCount, setFailCount] = useState(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const fetcherRef = useRef(fetcher);
  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);

  fetcherRef.current = fetcher;

  const fetchData = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const result = await fetcherRef.current();
      if (mountedRef.current) {
        setData(result);
        setError(null);
        setFailCount(0);
      }
    } catch (e: any) {
      if (mountedRef.current) {
        const msg = e.message || 'Failed to fetch';
        setError(msg);
        setFailCount(c => c + 1);
      }
    } finally {
      inFlightRef.current = false;
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    mountedRef.current = true;

    async function tick() {
      await fetchData();
      if (!cancelled) {
        timeoutRef.current = setTimeout(tick, intervalMs);
      }
    }

    tick();

    return () => {
      cancelled = true;
      mountedRef.current = false;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [fetchData, intervalMs, ...deps]);

  return { data, error, loading, failCount, refetch: fetchData };
}
